/**
 * target-options-i18n.ts — traduction d'affichage FR→EN des options
 * "Taille d'entreprise" et "Secteur d'activité".
 *
 * Ces valeurs sont SAISIES/STOCKÉES en français en base (target.companySize,
 * target.industry, challenge fictiveCompany, etc.). On ne change donc PAS la
 * `value` (sinon rupture des données existantes) — on traduit uniquement le
 * LABEL affiché selon la langue active.
 *
 * 3 listes coexistent dans le code (AccountPage, InterChallengeWizard,
 * AdminPage TARGET_INDUSTRY_OPTIONS) avec des libellés divergents : le
 * dictionnaire ci-dessous est leur union. Toute valeur absente du dict
 * retombe sur le texte FR brut (fallback gracieux, jamais cassé).
 */

const COMPANY_SIZE_EN: Record<string, string> = {
  "Micro-entreprises": "Micro-enterprises",
  "TPE/TPI": "Very small businesses",
  "PME/PMI": "Small & medium businesses",
  "ETI": "Mid-market companies",
  "Grandes entreprises": "Large enterprises",
  "Secteur public": "Public sector",
};

const INDUSTRY_EN: Record<string, string> = {
  // AccountPage
  "Administration - fonction publique": "Administration - public service",
  "Agroalimentaire": "Food & agriculture",
  "Artisanat d'art": "Arts & crafts",
  "Associations": "Nonprofits / associations",
  "Banques – assurances - services financiers": "Banking - insurance - financial services",
  "Chimie - plastique - conditionnement": "Chemicals - plastics - packaging",
  "Commerce de détail - grande distribution": "Retail - mass distribution",
  "Communication - marketing - information": "Communication - marketing - media",
  "Construction – bâtiment - travaux publics": "Construction - building - public works",
  "Culture – sports – loisirs": "Culture - sports - leisure",
  "Energie": "Energy",
  "Enseignement - formation": "Education - training",
  "Environnement – récupération – tri – recyclage - traitement des déchets – matériaux - de l'eau": "Environment - recycling - waste & water treatment",
  "Équipement - matériel pour activités professionnelles": "Professional equipment & supplies",
  "Fabrication - commerce de gros d'articles destinés à la vente": "Manufacturing - wholesale trade",
  "Gestion - administration des entreprises": "Business management & administration",
  "Hôtellerie - restauration - tourisme": "Hospitality - catering - tourism",
  "Immobilier": "Real estate",
  "Industrie textile": "Textile industry",
  "Informatique": "IT / Software",
  "Ingénieurs d'études et de recherche - chercheurs": "Engineering & research",
  "Logistique - transports": "Logistics - transportation",
  "Matériel électrique – électronique - optique": "Electrical - electronics - optics",
  "Mécanique, métallurgie": "Mechanics & metallurgy",
  "Minerais – minéraux - sidérurgie": "Ores - minerals - steel industry",
  "Professions juridiques": "Legal professions",
  "Santé, action sociale": "Healthcare & social work",
  "Services aux particuliers – collectivités - entreprises": "Services to individuals - communities - businesses",
  // InterChallengeWizard
  "Automobile": "Automotive",
  "Banque / Assurance": "Banking / Insurance",
  "BTP / Construction": "Construction / Building",
  "Chimie / Pharmacie": "Chemicals / Pharmaceuticals",
  "Commerce / Distribution": "Retail / Distribution",
  "Conseil / Audit": "Consulting / Audit",
  "Éducation / Formation": "Education / Training",
  "Énergie / Environnement": "Energy / Environment",
  "Industrie / Manufacture": "Industry / Manufacturing",
  "Informatique / Tech / SaaS": "IT / Tech / SaaS",
  "Logistique / Transport": "Logistics / Transportation",
  "Luxe / Mode": "Luxury / Fashion",
  "Médias / Communication": "Media / Communication",
  "Santé / Médical": "Healthcare / Medical",
  "Services aux entreprises": "Business services",
  "Télécom": "Telecom",
  "Tourisme / Hôtellerie / Restauration": "Tourism / Hospitality / Catering",
  // AdminPage TARGET_INDUSTRY_OPTIONS
  "Administration publique": "Public administration",
  "Agriculture": "Agriculture",
  "Armée/Sécurité": "Military / Security",
  "Audit/Gestion": "Audit / Management",
  "Automotive": "Automotive",
  "Banque/Assurance": "Banking / Insurance",
  "BTP/Architecture": "Construction / Architecture",
  "Chimie": "Chemicals",
  "Commerce de gros": "Wholesale trade",
  "Communication/Marketing/Pub": "Communication / Marketing / Advertising",
  "Construction (aéronautique/ferroviaire/navale)": "Construction (aerospace / rail / naval)",
  "Culture/Arts/Design": "Culture / Arts / Design",
  "Droit/Justice": "Law / Justice",
  "Edition/Journalisme": "Publishing / Journalism",
  "Education/Formation": "Education / Training",
  "Environnement": "Environment",
  "Hôtellerie/Restauration": "Hospitality / Catering",
  "Industrie": "Industry",
  // Les quatre libellés du seed de la banque de cas. Sans eux, `localizeIndustry` renvoyait la
  // valeur brute : un cas Kestio affichait « Informatique - télécoms - numérique » au milieu
  // d'un écran anglais (constaté le 02/09 sur le cas Neolinq).
  "Informatique - télécoms - numérique": "IT - telecoms - digital",
  "Industrie - production - fabrication": "Industry - production - manufacturing",
  "Santé - pharmaceutique - biotechnologies": "Healthcare - pharmaceuticals - biotech",
  "Commerce - distribution - retail": "Trade - distribution - retail",
  "Informatique & Télécoms": "IT & Telecoms",
  "Logistique/Transport": "Logistics/Transport",
  "Maintenance/Entretien": "Maintenance / Upkeep",
  "Mode/Industrie textile": "Fashion / Textile industry",
  "Santé/Pharmaceutique": "Healthcare / Pharmaceuticals",
  "Services professionnels": "Professional services",
  "ESS/ONG": "Social economy / NGOs",
  "Sport/Loisirs/Tourisme": "Sports / Leisure / Tourism",
};

/** Vrai si la langue demandée est l'anglais, quel que soit le variant régional.
 *  ⚠️ Un test `lang === "en"` strict laissait "en-US"/"en-GB" en FRANÇAIS : le détecteur
 *  navigateur d'i18next n'est pas contraint par `supportedLngs`, il peut donc renvoyer un
 *  code régional. Aligné sur pickOfferName/pickOfferDescription qui font déjà startsWith. */
function isEnglish(lang: string | null | undefined): boolean {
  return (lang || "").toLowerCase().startsWith("en");
}

/** Traduit un libellé de taille d'entreprise selon la langue (fallback : valeur brute). */
export function localizeCompanySize(value: string | null | undefined, lang: string): string {
  if (!value) return "";
  if (isEnglish(lang)) return COMPANY_SIZE_EN[value] || value;
  return value;
}

/** Traduit un libellé de secteur d'activité selon la langue (fallback : valeur brute). */
export function localizeIndustry(value: string | null | undefined, lang: string): string {
  if (!value) return "";
  // Marqueur "toutes industries" ("Tous" / "Tous secteurs" / "All" / "All industries") :
  // libellé spécial hors dictionnaire. En EN on affiche "All industries", en FR on
  // conserve la valeur stockée (pour ne pas régresser l'affichage français).
  if (ALL_INDUSTRIES_MARKERS.has(value)) {
    return isEnglish(lang) ? "All industries" : value;
  }
  if (isEnglish(lang)) return INDUSTRY_EN[value] || value;
  return value;
}

/**
 * Liste UNION de TOUS les secteurs d'activité connus (union des 3 listes
 * AccountPage / InterChallengeWizard / AdminPage). Sert à expanser le marqueur
 * "Tous" / "Tous secteurs" / "All" / "All industries" dans le payload N8N
 * (cf. expandAllIndustriesMarker ci-dessous).
 */
export const ALL_INDUSTRIES_UNION: string[] = Object.keys(INDUSTRY_EN);

/** Marqueurs de sélection "tous les secteurs" (FR + EN, version courte + longue). */
const ALL_INDUSTRIES_MARKERS = new Set([
  "Tous",
  "Tous secteurs",
  "All",
  "All industries",
]);

/**
 * Expanse un libellé "Tous" / "All" en LA LISTE complète des secteurs (joints
 * par ", "). N8N ne sait pas interpréter "Tous" dans son prompt — il faut lui
 * fournir la liste explicite. Si le marqueur n'est pas présent, retourne la
 * chaîne inchangée. Tolère une chaîne déjà jointe ("Tous, Immobilier" → liste
 * complète) comme un tableau JSON.stringify'd ("[\"Tous\"]").
 */
export function expandAllIndustriesMarker(industry: string | null | undefined): string {
  if (!industry) return "";
  // Découpe : supporte les valeurs JSON.stringify'd ET les valeurs déjà jointes par ", ".
  let parts: string[];
  try {
    const parsed = JSON.parse(industry);
    parts = Array.isArray(parsed) ? parsed.map((x) => String(x)) : [industry];
  } catch {
    parts = industry.split(",").map((x) => x.trim());
  }
  const hasAllMarker = parts.some((p) => ALL_INDUSTRIES_MARKERS.has(p));
  if (hasAllMarker) {
    return ALL_INDUSTRIES_UNION.join(", ");
  }
  return parts.filter(Boolean).join(", ");
}
